/**
 * Locale-aware relative-time formatting for "N minutes ago" style labels.
 *
 * Split in two so the unit-selection logic is pure + trivially testable and
 * the locale rendering is a thin `Intl.RelativeTimeFormat` wrapper (also pure —
 * deterministic given its inputs, no I/O, no `Date.now()` read inside).
 *
 * Used by the in-app inbox readers in customer-pwa + dashboard (surfaces the
 * `users/{uid}/inbox` messages the marketing/growth journeys already write).
 * Lives in `@kilat/shared` so the two apps share ONE implementation rather than
 * each growing its own copy (unified-primitives rule: extract before copy #2).
 */

/** The subset of `Intl.RelativeTimeFormatUnit` this helper ever emits. */
export type RelativeTimeUnit = 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year';

export interface RelativeTimeParts {
  /** Signed magnitude: negative for the past, positive for the future. */
  readonly value: number;
  readonly unit: RelativeTimeUnit;
}

// Whole seconds per unit. `month` = 30.44-day average; `year` = 365.25 days.
const SECONDS: readonly (readonly [RelativeTimeUnit, number])[] = [
  ['year', 31_557_600],
  ['month', 2_629_800],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
];

/**
 * Choose the coarsest unit that describes the gap between `fromMs` and `nowMs`.
 * Anything under 45s collapses to `{ value: 0, unit: 'second' }` so a
 * `numeric: 'auto'` formatter renders "now" rather than "12 seconds ago".
 *
 * Pure: same inputs → same output. Callers pass `Date.now()` in as `nowMs`.
 */
export function relativeTimeParts(fromMs: number, nowMs: number): RelativeTimeParts {
  const diffMs = fromMs - nowMs;
  const absSec = Math.abs(diffMs) / 1000;
  if (absSec < 45) return { value: 0, unit: 'second' };
  const sign = diffMs < 0 ? -1 : 1;
  for (const [unit, secs] of SECONDS) {
    if (absSec >= secs) return { unit, value: sign * Math.round(absSec / secs) };
  }
  // The 45s..59s window is below the `minute` row's 60s threshold but past the
  // "now" cutoff — round it up to 1 minute (never 0).
  return { unit: 'minute', value: sign * Math.max(1, Math.round(absSec / 60)) };
}

/**
 * Render a relative-time label in the given locale, e.g. "3 hours ago".
 * Falls back to `en` behaviour when the runtime lacks `Intl.RelativeTimeFormat`.
 */
export function formatRelativeTime(fromMs: number, nowMs: number, locale: string): string {
  const { value, unit } = relativeTimeParts(fromMs, nowMs);
  try {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(value, unit);
  } catch {
    // Extremely defensive — every modern browser + Node 20 ships RTF.
    return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(value, unit);
  }
}
